import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useIsAuthenticated, useMfaRequired, useAuthStore, useSessionTimeoutWarning } from '@/app/store/auth'
import { useSessionTimeout, useUserActivity } from '@/hooks/useSessionTimeout'
import { useAuthInitialization } from '@/hooks/useAuthInitialization'
import { AppShell } from '@/components/layout/AppShell'
import { SessionTimeoutDialog } from '@/components/dialogs/SessionTimeoutDialog'
import { LoginPage } from '@/pages/LoginPage'
import { MfaPage } from '@/pages/MfaPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DesignSystemPage } from '@/pages/DesignSystemPage'
import { PaletteEditorPage } from '@/pages/PaletteEditorPage'
import { SimpleProfilePage } from '@/features/profile/pages/SimpleProfilePage'
import { SettingsPage } from '@/features/settings/pages/SettingsPage'
import SecurityDashboardPage from '@/features/security/pages/SecurityDashboardPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { VaultListPage } from '@/features/vaults/pages/VaultListPage'
import { JobListPage } from '@/features/jobs/pages/JobListPage'
import { UserListPage } from '@/features/users/pages/UserListPage'
import { LayerListPage } from '@/features/layers/pages/LayerListPage'
import { RoleListPage } from '@/features/roles/pages/RoleListPage'
import { LocationListPage } from '@/features/locations/pages/LocationListPage'
import { ObjectListPage } from '@/features/objects/pages/ObjectListPage'
import { ObjectViewPage } from '@/features/objects/pages/ObjectViewPage'
import { TeamListPage } from '@/features/teams/pages/TeamListPage'
import { TechnologyListPage } from '@/features/technologies/pages/TechnologyListPage'
import { ContactListPage } from '@/features/contacts/pages/ContactListPage'
import { MasterRunbookListPage } from '@/features/master-runbooks/pages/MasterRunbookListPage'
import { ServiceNowCMDBPage } from '@/features/servicenow-cmdb/pages/ServiceNowCMDBPage'
import { RecoverRequestListPage } from '@/features/recover-requests/pages/RecoverRequestListPage'
import { RequestApprovalListPage } from '@/features/request-approvals/pages/RequestApprovalListPage'
import { MyAssignmentsPage } from '@/features/my-assignments/pages/MyAssignmentsPage'
import { UpdateRunBookPage } from '@/features/update-runbook/pages/UpdateRunBookPage'
import { RecoveryRunbookPage } from '@/features/recover-requests/pages/RecoveryRunbookPage'
import { RecoveryManagementPage } from '@/features/recover-requests/pages/RecoveryManagementPage'
import { RecoverySetDetailPage } from '@/features/recover-requests/pages/RecoverySetDetailPage'
import { UnassignedRequestDetailPage } from '@/features/recover-requests/pages/UnassignedRequestDetailPage'
import { AiModelsPage } from '@/features/ai-models/pages/AiModelsPage'
import { AiKnowledgeBasePage } from '@/features/ai-knowledge-base/pages/AiKnowledgeBasePage'
import { ForensicsListPage } from '@/features/forensics/pages/ForensicsListPage'
// ModelChangesPage now integrated as tab in AiModelsPage
import { IngressMonitoringPage } from '@/features/ingress-monitoring/pages/IngressMonitoringPage'
import { IaCPage } from '@/features/iac/pages/IaCPage'
import { IaCListPage } from '@/features/iac/pages/IaCListPage'
import { AnalyticsPage } from '@/features/analytics/pages/AnalyticsPage'
import { SchedulersPage } from '@/features/schedulers/pages/SchedulersPage'
import { AdminRoute } from '@/components/auth/AdminRoute'

/**
 * Remounts IaCPage whenever the project identity changes. The route param is
 * only `:mainId/:scenario`, but a project is identified by the `?pid=` query —
 * so switching projects (e.g. creating a new one from the list) changes only
 * the query string and would otherwise reuse the mounted component, leaking the
 * previous project's chat/files/stepper state. Keying on path+search forces a
 * clean remount (fresh state) per project. IaCPage never mutates `pid`
 * mid-run, so this never interrupts an in-progress generation.
 */
function IaCPageKeyed() {
    const location = useLocation()
    return <IaCPage key={`${location.pathname}${location.search}`} />
}

// Lazy-loaded feature modules (visualization + AI chat)
const VisualizationPage = lazy(() =>
    import('@/features/visualization/pages/VisualizationPage').then((m) => ({
        default: m.VisualizationPage,
    }))
)
const AiChatPage = lazy(() =>
    import('@/features/ai-chat/pages/AiChatPage').then((m) => ({
        default: m.AiChatPage,
    }))
)

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()
    const { accessToken, refreshToken, user } = useAuthStore((state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user
    }))

    // Handle session timeout for authenticated users
    useSessionTimeout()
    useUserActivity()

    // Check if we have isAuthenticated but no valid tokens (inconsistent state)
    if (isAuthenticated && (!accessToken || !refreshToken)) {
        return <Navigate to="/login" replace />
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    if (mfaRequired) {
        return <Navigate to="/mfa" replace />
    }

    return <AppShell>{children}</AppShell>
}

// Protected route WITHOUT AppShell — for standalone pages (e.g. AI chat in new tab)
function StandaloneProtectedRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()
    const { accessToken, refreshToken } = useAuthStore((state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
    }))

    useSessionTimeout()
    useUserActivity()

    if (isAuthenticated && (!accessToken || !refreshToken)) {
        return <Navigate to="/login" replace />
    }
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }
    if (mfaRequired) {
        return <Navigate to="/mfa" replace />
    }

    return <>{children}</>
}

// Public route component (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useIsAuthenticated()
    const mfaRequired = useMfaRequired()

    if (isAuthenticated && !mfaRequired) {
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

export default function App() {
    // Initialize and validate authentication state on app startup
    useAuthInitialization()

    // Session timeout warning state
    const sessionTimeoutWarning = useSessionTimeoutWarning()
    const { extendSession, logout } = useAuthStore()

    const handleExtendSession = () => {
        extendSession()
    }

    const handleLogout = () => {
        logout()
    }

    return (
        <>
            <Routes>
                {/* Public routes */}
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    }
                />
                <Route
                    path="/mfa"
                    element={
                        <PublicRoute>
                            <MfaPage />
                        </PublicRoute>
                    }
                />

                {/* Protected routes */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <DashboardPage />
                        </ProtectedRoute>
                    }
                />

                {/* Placeholder routes for future features */}
                <Route
                    path="/jobs"
                    element={
                        <ProtectedRoute>
                            <JobListPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/policies"
                    element={
                        <ProtectedRoute>
                            <div className="p-6">
                                <h1 className="text-2xl font-bold">Policies</h1>
                                <p className="text-muted-foreground">Policy management coming soon...</p>
                            </div>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/targets"
                    element={
                        <ProtectedRoute>
                            <div className="p-6">
                                <h1 className="text-2xl font-bold">Targets</h1>
                                <p className="text-muted-foreground">Target management coming soon...</p>
                            </div>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/vaults"
                    element={
                        <ProtectedRoute>
                            <VaultListPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <UserListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/layers"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <LayerListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/locations"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <LocationListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/roles"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <RoleListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/teams"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <TeamListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/technologies"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <TechnologyListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/contacts"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ContactListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/master-runbooks"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <MasterRunbookListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/servicenow-cmdb"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ServiceNowCMDBPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ai-models"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AiModelsPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ai-knowledge-base"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <AiKnowledgeBasePage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/forensics"
                    element={
                        <ProtectedRoute>
                            <ForensicsListPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/analytics"
                    element={
                        <ProtectedRoute>
                            <AnalyticsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/ingress-monitoring"
                    element={
                        <ProtectedRoute>
                            <IngressMonitoringPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management"
                    element={
                        <ProtectedRoute>
                            <RecoveryManagementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management/requests/:requestID"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <UnassignedRequestDetailPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-management/sets/:setId"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <RecoverySetDetailPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/assign-engineer"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <RecoverRequestListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/request-approvals"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <RequestApprovalListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/my-assignments"
                    element={
                        <ProtectedRoute>
                            <MyAssignmentsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/update-runbook"
                    element={
                        <ProtectedRoute>
                            <UpdateRunBookPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/recovery-runbook/:requestId"
                    element={
                        <ProtectedRoute>
                            <RecoveryRunbookPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/objects"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ObjectListPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/objects/:id"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <ObjectViewPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/design-system"
                    element={
                        <ProtectedRoute>
                            <DesignSystemPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/palette-editor"
                    element={
                        <ProtectedRoute>
                            <AdminRoute>
                                <PaletteEditorPage />
                            </AdminRoute>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/security"
                    element={
                        <ProtectedRoute>
                            <SecurityDashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile-settings"
                    element={
                        <ProtectedRoute>
                            <SimpleProfilePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <AdminRoute>
                            <SettingsPage />
                        </AdminRoute>
                    }
                />

                {/* Infrastructure as Code */}
                <Route
                    path="/iac"
                    element={
                        <ProtectedRoute>
                            <IaCListPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/iac/:mainId/:scenario"
                    element={
                        <ProtectedRoute>
                            <IaCPageKeyed />
                        </ProtectedRoute>
                    }
                />

                {/* Schedulers */}
                <Route
                    path="/schedulers"
                    element={
                        <ProtectedRoute>
                            <SchedulersPage />
                        </ProtectedRoute>
                    }
                />

                {/* Visualization */}
                <Route
                    path="/visualization"
                    element={
                        <ProtectedRoute>
                            <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                                <VisualizationPage />
                            </Suspense>
                        </ProtectedRoute>
                    }
                />

                {/* AI Chat — standalone (no sidebar/topbar) for open-in-new-tab */}
                <Route
                    path="/ai-chat"
                    element={
                        <StandaloneProtectedRoute>
                            <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
                                <AiChatPage />
                            </Suspense>
                        </StandaloneProtectedRoute>
                    }
                />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>

            {/* Session Timeout Dialog */}
            <SessionTimeoutDialog
                isOpen={sessionTimeoutWarning.isActive}
                onExtendSession={handleExtendSession}
                onLogout={handleLogout}
                timeRemaining={sessionTimeoutWarning.timeRemaining}
            />
        </>
    )
}
